import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import { Icons } from '../Icons';
import { DisplaySetMessageListTooltip } from '../DisplaySetMessageListTooltip';
import { TooltipTrigger, TooltipContent, Tooltip } from '../Tooltip';

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = ({
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  loadingProgress,
  countIcon,
  messages,
  isActive,
  onClick,
  onDoubleClick,
  thumbnailType,
  modality,
  viewPreset = 'thumbnails',
  isHydratedForDerivedDisplaySet = false,
  isTracked = false,
  canReject = false,
  dragData = {},
  onReject = () => {},
  onClickUntrack = () => {},
  ThumbnailMenuItems = () => {},
}: withAppTypes): React.ReactNode => {
  // TODO: We should wrap our thumbnail to create a "DraggableThumbnail", as
  // this will still allow for "drag", even if there is no drop target for the
  // specified item.
  const [collectedProps, drag, dragPreview] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
  });

  const [lastTap, setLastTap] = useState(0);

  const handleTouchEnd = e => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 500 && tapLength > 0) {
      onClick(e);
    } else {
      onDoubleClick(e);
    }
    setLastTap(currentTime);
  };

  const renderThumbnailPreset = () => {
    return (
      <div
        className={classnames(
          'group relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-2xl bg-[#000] transition-all duration-200 ease-linear',
          isActive ? 'border-highlight/60 border-2' : 'hover:scale-90'
        )}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={imageAltText}
            className="w-[90%] rounded-md object-contain"
            crossOrigin="anonymous"
          />
        ) : (
          <div className="m-auto w-[80%] rounded object-contain"></div>
        )}
        {/* Overlay */}
        <div
          className={classnames(
            'bg-primary/20 pointer-events-none absolute inset-0 z-10 rounded-2xl opacity-0 transition-opacity duration-100 group-hover:opacity-100'
          )}
        />

        {/* top left */}
        <div className="absolute top-3 left-0 flex h-3 w-full items-center justify-between px-2 py-1">
          <div className="text-muted-foreground text-xs font-normal"> S: {seriesNumber}</div>
          <div className="text-muted-foreground text-xs">
            <div className="flex items-center gap-[4px]">
              {countIcon ? (
                React.createElement(Icons[countIcon] || Icons.MissingIcon, {
                  className: 'w-3',
                })
              ) : (
                <Icons.InfoSeries className="w-3" />
              )}
              <div>{numInstances}</div>
            </div>
          </div>
          {/* <div className="text-common-bright/80 text-right text-xs font-normal">{modality}</div> */}
        </div>
        {/* top right */}
        {/* top center */}
        <div
          className={classnames(
            'absolute top-1 left-[calc(50%-4px)] h-2 w-2 rounded',
            isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/50',
            loadingProgress && loadingProgress < 1 && 'bg-highlight/50'
          )}
        ></div>

        {/* middle left-right
        <div className="absolute top-[calc(50%-10px)] left-0 flex h-4 w-full justify-between px-1">
          <div className="text-muted-foreground text-xs"> S:{seriesNumber}</div>
          <div className="text-muted-foreground text-xs">
            <div className="flex items-center gap-[4px]">
              {countIcon ? (
                React.createElement(Icons[countIcon] || Icons.MissingIcon, {
                  className: 'w-3',
                })
              ) : (
                <Icons.InfoSeries className="w-3" />
              )}
              <div>{numInstances}</div>
            </div>
          </div>
        </div>
*/}
        <div className="absolute bottom-0 left-0 flex h-6 w-full flex-col items-center justify-end text-[10px]">
          <div className="absolute bottom-1 left-0 w-full rounded-lg bg-black bg-opacity-50">
            <div className="text-common-bright/80 ml-2 break-all text-center">{description}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderListPreset = () => {
    return (
      <div
        className={classnames(
          'relative flex h-full w-full flex-col items-center justify-between overflow-hidden rounded-2xl transition-all duration-200 ease-linear',
          isActive && 'border-primary border-2'
        )}
      >
        <div
          className={classnames(
            'h-[12px] w-[4px] min-w-[4px] rounded',
            isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
            loadingProgress && loadingProgress < 1 && 'bg-primary/25'
          )}
        ></div>

        <div className="absolute top-2 flex w-full justify-between px-2">
          <div className="text-muted-foreground text-[12px]"> S:{seriesNumber}</div>
          <div className="text-muted-foreground text-[12px]">
            <div className="flex items-center gap-[4px]">
              {' '}
              {countIcon ? (
                React.createElement(Icons[countIcon] || Icons.MissingIcon, { className: 'w-3' })
              ) : (
                <Icons.InfoSeries className="w-3" />
              )}
              <div>{numInstances}</div>
            </div>
          </div>
        </div>

        <div className="absolute top-4 left-0 flex w-full items-center justify-center text-center text-xs font-semibold text-white/60">
          {modality}
        </div>

        <div className="absolute bottom-2 left-0 w-full overflow-hidden overflow-ellipsis break-all px-2 text-center text-xs font-normal text-white/60">
          {description}
        </div>
      </div>
    );
  };

  return (
    <div
      className={classnames(
        className,
        'group flex cursor-pointer select-none flex-col rounded-2xl bg-transparent outline-none',
        viewPreset === 'thumbnails' && 'h-28 w-28 sm:h-32 sm:w-32',
        viewPreset === 'list' && 'h-20 w-28 sm:w-32'
      )}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={
        thumbnailType === 'thumbnailNoImage'
          ? 'study-browser-thumbnail-no-image'
          : 'study-browser-thumbnail'
      }
      data-series={seriesNumber}
      onClick={onDoubleClick}
      onDoubleClick={onDoubleClick}
      role="button"
    >
      <div
        ref={drag}
        className="h-full w-full"
      >
        {viewPreset === 'thumbnails' && renderThumbnailPreset()}
        {viewPreset === 'list' && renderListPreset()}
      </div>
    </div>
  );
};

Thumbnail.propTypes = {
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  /**
   * Data the thumbnail should expose to a receiving drop target. Use a matching
   * `dragData.type` to identify which targets can receive this draggable item.
   * If this is not set, drag-n-drop will be disabled for this thumbnail.
   *
   * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
   */
  dragData: PropTypes.shape({
    /** Must match the "type" a dropTarget expects */
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: PropTypes.any,
  numInstances: PropTypes.number.isRequired,
  loadingProgress: PropTypes.number,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  modality: PropTypes.string,
  isHydratedForDerivedDisplaySet: PropTypes.bool,
  isTracked: PropTypes.bool,
  onClickUntrack: PropTypes.func,
  countIcon: PropTypes.string,
  thumbnailType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage']),
};

export { Thumbnail };
